<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { parseBoolean } from '@chatwoot/utils';
import mfaAPI from 'dashboard/api/mfa';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { otpSecretFromProvisioningUri } from 'shared/helpers/mfa';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import MfaStatusCard from './MfaStatusCard.vue';
import MfaSetupWizard from './MfaSetupWizard.vue';
import MfaManagementActions from './MfaManagementActions.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

// State
const mfaEnabled = ref(false);
const mfaEnrolled = ref(false);
const backupCodesGenerated = ref(false);
const showSetup = ref(false);
const isRebinding = ref(false);
const provisioningUri = ref('');
const qrCodeUrl = ref('');
const secretKey = ref('');
const backupCodes = ref([]);
// Component refs
const setupWizardRef = ref(null);
const managementActionsRef = ref(null);
const enableDialogRef = ref(null);
const reactivateDialogRef = ref(null);
const enablePassword = ref('');
const setupPassword = ref('');
const reactivatePassword = ref('');
const reactivateOtp = ref('');

// Load MFA status on mount
onMounted(async () => {
  // Check if MFA is enabled globally
  if (!parseBoolean(window.chatwootConfig?.isMfaEnabled)) {
    // Redirect to profile settings if MFA is disabled
    router.push({
      name: 'profile_settings_index',
      params: {
        accountId: route.params.accountId,
      },
    });
    return;
  }

  try {
    const response = await mfaAPI.get();
    mfaEnabled.value = response.data.enabled;
    mfaEnrolled.value = response.data.enrolled;
    backupCodesGenerated.value = response.data.backup_codes_generated;
  } catch (error) {
    // Handle error silently
  }
});

const openEnableDialog = () => {
  enablePassword.value = '';
  reactivatePassword.value = '';
  reactivateOtp.value = '';
  if (mfaEnrolled.value) {
    reactivateDialogRef.value?.open();
    return;
  }
  enableDialogRef.value?.open();
};

const applyProvisioningResponse = response => {
  provisioningUri.value =
    response.data.provisioning_uri || response.data.provisioning_url;

  if (response.data.qr_code_url) {
    qrCodeUrl.value = response.data.qr_code_url;
  }

  secretKey.value =
    response.data.secret || otpSecretFromProvisioningUri(provisioningUri.value);
};

const startMfaSetup = async () => {
  try {
    const response = await mfaAPI.enable(enablePassword.value);
    applyProvisioningResponse(response);
    setupPassword.value = enablePassword.value;
    enablePassword.value = '';
    isRebinding.value = false;
    backupCodes.value = [];
    showSetup.value = true;
    enableDialogRef.value?.close();
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('MFA_SETTINGS.SETUP.ERROR_STARTING')
    );
  }
};

const verifyCode = async (verificationCode, { resolve, reject } = {}) => {
  try {
    const response = isRebinding.value
      ? await mfaAPI.rebindVerify(verificationCode, setupPassword.value)
      : await mfaAPI.verify(verificationCode, setupPassword.value);
    if (response.data.backup_codes) {
      backupCodes.value = response.data.backup_codes;
    }
    resolve?.();
  } catch (error) {
    const message =
      error.response?.data?.error || t('MFA_SETTINGS.SETUP.INVALID_CODE');
    setupWizardRef.value?.handleVerificationError(message);
    reject?.(error);
  }
};

// Complete MFA setup
const reactivateMfa = async () => {
  try {
    await mfaAPI.verify(reactivateOtp.value, reactivatePassword.value);
    mfaEnabled.value = true;
    reactivatePassword.value = '';
    reactivateOtp.value = '';
    emitter.emit(BUS_EVENTS.MFA_STATE_CHANGED);
    useAlert(t('MFA_SETTINGS.REACTIVATE.SUCCESS'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('MFA_SETTINGS.REACTIVATE.ERROR'));
  }
};

const startFreshSetupFromReactivate = async () => {
  try {
    const response = await mfaAPI.beginReplacement(reactivatePassword.value);
    applyProvisioningResponse(response);
    setupPassword.value = reactivatePassword.value;
    reactivatePassword.value = '';
    reactivateOtp.value = '';
    mfaEnrolled.value = false;
    isRebinding.value = false;
    backupCodes.value = [];
    showSetup.value = true;
    reactivateDialogRef.value?.close();
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('MFA_SETTINGS.SETUP.ERROR_STARTING')
    );
  }
};

const completeMfaSetup = () => {
  const successMessage = isRebinding.value
    ? t('MFA_SETTINGS.REBIND.SUCCESS')
    : t('MFA_SETTINGS.SETUP.SUCCESS');
  mfaEnabled.value = true;
  mfaEnrolled.value = true;
  backupCodesGenerated.value = true;
  showSetup.value = false;
  setupPassword.value = '';
  isRebinding.value = false;
  emitter.emit(BUS_EVENTS.MFA_STATE_CHANGED);
  useAlert(successMessage);
};

// Cancel setup
const cancelSetup = () => {
  showSetup.value = false;
  setupPassword.value = '';
  isRebinding.value = false;
};

const startRebind = async ({ password, otpCode, backupCode }) => {
  try {
    const response = await mfaAPI.rebind(password, { otpCode, backupCode });
    applyProvisioningResponse(response);
    setupPassword.value = password;
    backupCodes.value = [];
    isRebinding.value = true;
    showSetup.value = true;
    managementActionsRef.value?.resetRebindForm();
  } catch (error) {
    useAlert(error.response?.data?.error || t('MFA_SETTINGS.REBIND.ERROR'));
  }
};

// Disable MFA
const disableMfa = async ({ password, otpCode, backupCode }) => {
  try {
    await mfaAPI.disable(password, { otpCode, backupCode });
    mfaEnabled.value = false;
    managementActionsRef.value?.resetDisableForm();
    emitter.emit(BUS_EVENTS.MFA_STATE_CHANGED);
    useAlert(t('MFA_SETTINGS.DISABLE.SUCCESS'));
  } catch (error) {
    useAlert(t('MFA_SETTINGS.DISABLE.ERROR'));
  }
};

// Regenerate backup codes
const regenerateBackupCodes = async ({ otpCode }) => {
  try {
    const response = await mfaAPI.regenerateBackupCodes(otpCode);
    backupCodes.value = response.data.backup_codes;
    managementActionsRef.value?.resetRegenerateForm();
    managementActionsRef.value?.showBackupCodesDialog();
    emitter.emit(BUS_EVENTS.MFA_STATE_CHANGED);
    useAlert(t('MFA_SETTINGS.REGENERATE.SUCCESS'));
  } catch (error) {
    useAlert(t('MFA_SETTINGS.REGENERATE.ERROR'));
  }
};
</script>

<template>
  <div class="grid w-full">
    <BaseSettingsHeader
      :title="$t('MFA_SETTINGS.TITLE')"
      :description="$t('MFA_SETTINGS.SUBTITLE')"
      :back-button-label="$t('PROFILE_SETTINGS.TITLE')"
    />

    <div class="grid gap-4 w-full mt-4">
      <!-- MFA Status Card -->
      <MfaStatusCard
        :mfa-enabled="mfaEnabled"
        :show-setup="showSetup"
        @enable-mfa="openEnableDialog"
      />

      <!-- MFA Setup Wizard -->
      <MfaSetupWizard
        ref="setupWizardRef"
        :show-setup="showSetup"
        :provisioning-uri="provisioningUri"
        :secret-key="secretKey"
        :backup-codes="backupCodes"
        :qr-code-url-prop="qrCodeUrl"
        @cancel="cancelSetup"
        @verify="verifyCode"
        @complete="completeMfaSetup"
      />

      <!-- MFA Management Actions -->
      <MfaManagementActions
        ref="managementActionsRef"
        :mfa-enabled="mfaEnabled && !showSetup"
        :backup-codes="backupCodes"
        @disable-mfa="disableMfa"
        @regenerate-backup-codes="regenerateBackupCodes"
        @rebind-authenticator="startRebind"
      />

      <Dialog
        ref="reactivateDialogRef"
        type="edit"
        :title="$t('MFA_SETTINGS.REACTIVATE.TITLE')"
        :description="$t('MFA_SETTINGS.REACTIVATE.DESCRIPTION')"
        :confirm-button-label="$t('MFA_SETTINGS.REACTIVATE.CONFIRM')"
        :cancel-button-label="$t('MFA_SETTINGS.REACTIVATE.CANCEL')"
        :disable-confirm-button="
          !reactivatePassword || reactivateOtp.length !== 6
        "
        @confirm="reactivateMfa"
      >
        <div class="flex flex-col gap-4">
          <Input
            v-model="reactivatePassword"
            type="password"
            :label="$t('MFA_SETTINGS.REACTIVATE.PASSWORD')"
          />
          <Input
            v-model="reactivateOtp"
            type="text"
            maxlength="6"
            :label="$t('MFA_SETTINGS.REACTIVATE.OTP_CODE')"
          />
          <div class="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              link
              slate
              :label="$t('MFA_SETTINGS.REACTIVATE.USE_NEW_AUTHENTICATOR')"
              :disabled="!reactivatePassword"
              @click.prevent="startFreshSetupFromReactivate"
            />
            <p v-if="!reactivatePassword" class="mb-0 text-xs text-n-slate-11">
              {{ $t('MFA_SETTINGS.REACTIVATE.USE_NEW_AUTHENTICATOR_HINT') }}
            </p>
          </div>
        </div>
      </Dialog>

      <Dialog
        ref="enableDialogRef"
        type="edit"
        :title="$t('MFA_SETTINGS.ENABLE.PASSWORD_TITLE')"
        :description="$t('MFA_SETTINGS.ENABLE.PASSWORD_DESCRIPTION')"
        :confirm-button-label="$t('MFA_SETTINGS.ENABLE.CONFIRM')"
        :cancel-button-label="$t('MFA_SETTINGS.ENABLE.CANCEL')"
        :disable-confirm-button="!enablePassword"
        @confirm="startMfaSetup"
      >
        <Input
          v-model="enablePassword"
          type="password"
          :label="$t('MFA_SETTINGS.ENABLE.PASSWORD')"
        />
      </Dialog>
    </div>
  </div>
</template>
